/**
 * Shared null-safe phone formatter for US E.164 (+1XXXXXXXXXX).
 * @param {string|null|undefined} number
 * @returns {string}
 */
export function formatPhoneNumber(number) {
  if (number == null || typeof number !== 'string') {
    return '';
  }
  const cleaned = number.replace(/\D/g, '');
  let national = cleaned;
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    national = cleaned.slice(1);
  }
  if (national.length === 10) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return number;
}

/**
 * Safe fetch JSON parser — preserves HTTP status; does not mislabel server errors as network failures.
 * @param {Response} response
 * @returns {Promise<{ ok: boolean, status: number, data: object|null, parseError: string|null, contentType: string|null }>}
 */
export async function parseApiResponse(response) {
  const status = response.status;
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();
  if (!text) {
    return {
      ok: response.ok,
      status,
      data: null,
      parseError: response.ok ? null : 'empty_response',
      contentType
    };
  }
  if (!contentType.includes('application/json')) {
    return {
      ok: false,
      status,
      data: null,
      parseError: status >= 500 ? 'html_or_non_json_error' : 'non_json',
      contentType
    };
  }
  try {
    const data = JSON.parse(text);
    return { ok: response.ok, status, data, parseError: null, contentType };
  } catch {
    return {
      ok: false,
      status,
      data: null,
      parseError: 'json_parse_failed',
      contentType
    };
  }
}

export function userMessageForCode(code, fallback) {
  const map = {
    DUPLICATE_INMATE_ID: 'This inmate ID is already registered. Please use a different ID or contact support.',
    DUPLICATE_USERNAME: 'This username is already taken. Please choose a different username.',
    NUMBER_UNAVAILABLE: 'That phone number is no longer available. Please select a different number.',
    NUMBER_ALREADY_ASSIGNED: 'That phone number is already assigned. Please select a different number.',
    CHECKOUT_EXPIRED: 'This checkout session has expired. Please start again.',
    CHECKOUT_NOT_FOUND:
      'This checkout is unavailable or has expired. If you already paid, do not pay again — contact support with your checkout reference.',
    PAYMENT_PENDING: 'Your payment is still processing. Please wait and refresh this page.',
    PAYMENT_FAILED: 'Payment was not completed. Please try again.',
    PAYMENT_DECLINED: 'Your payment was declined. Please try a different payment method.',
    PROVISIONING_PENDING: 'Payment received. We are activating your phone number.',
    PROVISIONING_FAILED:
      'Payment succeeded but number setup failed. Do not pay again — contact support with your checkout reference.',
    RATE_LIMITED: 'Too many requests. Please wait and try again.',
    INVALID_REQUEST: 'Some registration details are missing or invalid. Please start signup again.',
    RAW_CARD_DATA_FORBIDDEN: 'Card details are collected securely by Stripe only.',
    AMOUNT_TAMPERING: 'Invalid price information. Please try again.',
    STRIPE_CONFIG_ERROR: 'Payment system is temporarily unavailable. Please try again later.',
    SECURE_CHECKOUT_DISABLED: 'Secure checkout is temporarily unavailable.',
    FREE_BETA_DISABLED: 'Free beta signup is not available.',
    BETA_CODE_REQUIRED: 'A valid beta code is required for free signup.',
    BETA_CODE_INVALID: 'That beta code is not valid.',
    BETA_CODE_EXPIRED: 'That beta code has expired.',
    BETA_CODE_ALREADY_USED: 'That beta code has already been used.',
    CHECKOUT_RESUME_AUTH_REQUIRED:
      'Sign in is required to continue checkout. If you already paid, do not pay again — contact support with your checkout reference.',
    UNAUTHENTICATED:
      'Sign in is required to continue checkout. If you already paid, do not pay again — contact support with your checkout reference.',
    UNAUTHORIZED:
      'You are not authorized for this checkout. If you already paid, do not pay again — contact support with your checkout reference.',
    PENDING_OR_INACTIVE: 'This account cannot start checkout yet.',
    INMATE_FORBIDDEN: 'Unable to complete this checkout request.',
    CHECKOUT_CONFLICT:
      'Another checkout is already in progress for this inmate. Continue that session or wait for it to expire.',
    LEGACY_CHECKOUT_INELIGIBLE:
      'This checkout cannot be resumed. Please start again from signup. If you already paid, do not pay again — contact support.',
    CSRF_REJECTED: 'Unable to complete this request. Refresh the page and try again.',
    ORIGIN_FORBIDDEN: 'Unable to complete this request.',
    INTERNAL_ERROR: 'Something went wrong. Please try again later.',
    LEGACY_ROUTE_DISABLED: 'This payment form is outdated. Please refresh the page and try again.'
  };
  return map[code] || fallback || map.INTERNAL_ERROR;
}

const STORAGE_KEY = 'skylink_checkout_attempt_id';
const FREE_BETA_RESUME_KEY = 'skylink_checkout_free_beta_v1';
const TOKEN_MAP_KEY = 'skylink_checkout_status_tokens_v1';
const PENDING_SIGNUP_KEY = 'skylink_pending_signup_v1';
const IDEMPOTENCY_KEY_STORAGE = 'skylink_checkout_idempotency_v1';

const TERMINAL_STATUSES = new Set([
  'provisioned',
  'provisioning_failed',
  'canceled',
  'expired',
  'refunded'
]);

/**
 * Opaque per-tab checkout submission idempotency key (UUID).
 * Never constructed from personal data; not an ownership secret.
 */
export function getOrCreateCheckoutIdempotencyKey() {
  try {
    let existing = sessionStorage.getItem(IDEMPOTENCY_KEY_STORAGE);
    if (
      existing &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        existing
      )
    ) {
      return existing;
    }
    const next =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
    sessionStorage.setItem(IDEMPOTENCY_KEY_STORAGE, next);
    return next;
  } catch {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export function clearCheckoutIdempotencyKey() {
  try {
    sessionStorage.removeItem(IDEMPOTENCY_KEY_STORAGE);
  } catch {
    /* ignore */
  }
}
/**
 * Store non-secret navigation recovery data. Password is never stored.
 */
export function stashPendingSignup(inmateData, selectedNumber) {
  if (!inmateData || !selectedNumber?.phoneNumber) return;
  const safe = {
    inmateConnectionId: inmateData.inmateConnectionId || '',
    inmateUuid: inmateData.inmateUuid || '',
    inmateName: inmateData.inmateName || '',
    inmateId: inmateData.inmateId || '',
    facilityName: inmateData.facilityName || '',
    state: inmateData.state || '',
    areaCode: inmateData.areaCode || '',
    phoneNumber: selectedNumber.phoneNumber,
    locality: selectedNumber.locality || null
  };
  try {
    sessionStorage.setItem(PENDING_SIGNUP_KEY, JSON.stringify(safe));
  } catch {
    /* ignore */
  }
}

export function loadPendingSignup() {
  try {
    const raw = sessionStorage.getItem(PENDING_SIGNUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveCheckoutAttemptId(id) {
  try {
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

export function loadCheckoutAttemptId() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Remember that the in-progress attempt is free-beta so resume skips Stripe. */
export function markFreeBetaCheckoutResume(enabled) {
  try {
    if (enabled) sessionStorage.setItem(FREE_BETA_RESUME_KEY, '1');
    else sessionStorage.removeItem(FREE_BETA_RESUME_KEY);
  } catch {
    /* ignore */
  }
}

export function loadFreeBetaCheckoutResume() {
  try {
    return sessionStorage.getItem(FREE_BETA_RESUME_KEY) === '1';
  } catch {
    return false;
  }
}

function readTokenMap() {
  try {
    const raw = sessionStorage.getItem(TOKEN_MAP_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeTokenMap(map) {
  try {
    sessionStorage.setItem(TOKEN_MAP_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/**
 * Store opaque status token keyed by checkout attempt id (sessionStorage only).
 * Never place token in URLs, Stripe config, or logs.
 */
export function saveStatusToken(checkoutAttemptId, token) {
  if (!checkoutAttemptId || !token) return;
  const map = readTokenMap();
  map[checkoutAttemptId] = token;
  writeTokenMap(map);
  saveCheckoutAttemptId(checkoutAttemptId);
}

/**
 * Load status token for a given attempt id.
 */
export function loadStatusToken(checkoutAttemptId) {
  if (!checkoutAttemptId) return null;
  const map = readTokenMap();
  return map[checkoutAttemptId] || null;
}

export function removeStatusToken(checkoutAttemptId) {
  if (!checkoutAttemptId) return;
  const map = readTokenMap();
  if (Object.prototype.hasOwnProperty.call(map, checkoutAttemptId)) {
    delete map[checkoutAttemptId];
    writeTokenMap(map);
  }
}

/**
 * Clear status token after a terminal checkout state.
 */
export function clearStatusTokenIfTerminal(checkoutAttemptId, status) {
  if (status && TERMINAL_STATUSES.has(status)) {
    removeStatusToken(checkoutAttemptId);
  }
}

export function clearCheckoutLocalState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_MAP_KEY);
    sessionStorage.removeItem(PENDING_SIGNUP_KEY);
    sessionStorage.removeItem(IDEMPOTENCY_KEY_STORAGE);
  } catch {
    /* ignore */
  }
}

/**
 * Build Authorization header for status GET (never put token in the URL).
 */
export function statusAuthHeader(statusToken) {
  if (!statusToken) return {};
  return { Authorization: `Bearer ${statusToken}` };
}

/**
 * Build status status path without query token.
 */
export function checkoutStatusPath(checkoutAttemptId) {
  return `/api/checkout/${encodeURIComponent(checkoutAttemptId)}`;
}

export { TERMINAL_STATUSES };
